import React, { useState, useRef } from 'react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from '../../components/ui/dialog';
import { Button } from '../../components/ui/button';
import { Upload, Loader2, CheckCircle2, XCircle, AlertTriangle, Download, FileSpreadsheet } from 'lucide-react';
import { toast } from 'sonner';
import { colaboradoresAPI } from '../../lib/api';

const b64ToBlob = (b64, mime) => {
  const bytes = atob(b64);
  const arr = new Uint8Array(bytes.length);
  for (let i = 0; i < bytes.length; i++) arr[i] = bytes.charCodeAt(i);
  return new Blob([arr], { type: mime });
};

const StatCard = ({ label, value, color, testid }) => (
  <div className={`rounded-lg border p-3 ${color}`} data-testid={testid}>
    <div className="text-2xl font-bold">{value}</div>
    <div className="text-xs mt-0.5">{label}</div>
  </div>
);

export default function ColaboradorImportDialog({ open, onClose, onImported }) {
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [result, setResult] = useState(null);
  const inputRef = useRef(null);

  const reset = () => { setFile(null); setResult(null); setUploading(false); };

  const handleClose = () => { reset(); onClose(); };

  const handleFile = (f) => {
    if (!f) return;
    if (!f.name.toLowerCase().endsWith('.xlsx')) {
      toast.error('Solo se permiten archivos .xlsx');
      return;
    }
    setFile(f);
    setResult(null);
  };

  const handleUpload = async () => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await colaboradoresAPI.importFile(fd);
      setResult(res.data);
      if (res.data.creados > 0) {
        toast.success(`${res.data.creados} colaborador(es) creado(s)`);
        onImported && onImported();
      } else {
        toast.warning('No se creó ningún colaborador. Revisa el log de errores.');
      }
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Error al procesar el archivo');
    } finally {
      setUploading(false);
    }
  };

  const downloadErrorLog = () => {
    if (!result?.error_log_xlsx_base64) return;
    const blob = b64ToBlob(result.error_log_xlsx_base64,
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet');
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'errores_importacion.xlsx';
    a.click();
    window.URL.revokeObjectURL(url);
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && handleClose()}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="colab-import-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Upload className="w-5 h-5" /> Importación masiva de colaboradores
          </DialogTitle>
        </DialogHeader>

        {!result && (
          <div className="space-y-4">
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-sm text-blue-800">
              El archivo debe usar la plantilla oficial (.xlsx). La importación <strong>solo da de alta</strong> colaboradores nuevos; si una CURP ya existe, esa fila se rechaza (usa Reingreso en la ficha).
            </div>

            <div
              className="border-2 border-dashed border-slate-300 rounded-xl p-8 text-center cursor-pointer hover:border-slate-400 transition-colors"
              onClick={() => inputRef.current?.click()}
              onDragOver={(e) => e.preventDefault()}
              onDrop={(e) => { e.preventDefault(); handleFile(e.dataTransfer.files?.[0]); }}
              data-testid="colab-import-dropzone"
            >
              <FileSpreadsheet className="w-10 h-10 mx-auto text-slate-400 mb-2" />
              {file ? (
                <p className="text-sm text-slate-700 font-medium">{file.name}</p>
              ) : (
                <p className="text-sm text-slate-500">Haz clic o arrastra tu archivo .xlsx aquí</p>
              )}
              <input
                ref={inputRef}
                type="file"
                accept=".xlsx"
                className="hidden"
                onChange={(e) => handleFile(e.target.files?.[0])}
                data-testid="colab-import-file-input"
              />
            </div>

            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={handleClose} data-testid="colab-import-cancel">Cancelar</Button>
              <Button onClick={handleUpload} disabled={!file || uploading} data-testid="colab-import-submit">
                {uploading ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Procesando…</> : <><Upload className="w-4 h-4 mr-2" /> Importar</>}
              </Button>
            </div>
          </div>
        )}

        {result && (
          <div className="space-y-4" data-testid="colab-import-result">
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
              <StatCard label="Total procesadas" value={result.total} color="bg-slate-50 border-slate-200 text-slate-700" testid="import-stat-total" />
              <StatCard label="Creados" value={result.creados} color="bg-emerald-50 border-emerald-200 text-emerald-700" testid="import-stat-creados" />
              <StatCard label="CURP existente" value={result.rechazados_curp_existente} color="bg-amber-50 border-amber-200 text-amber-700" testid="import-stat-existente" />
              <StatCard label="Duplicado en archivo" value={result.rechazados_duplicado_archivo} color="bg-amber-50 border-amber-200 text-amber-700" testid="import-stat-duplicado" />
              <StatCard label="Campos faltantes" value={result.rechazados_campos_faltantes} color="bg-red-50 border-red-200 text-red-700" testid="import-stat-faltantes" />
            </div>

            {result.creados > 0 && (
              <div className="flex items-center gap-2 text-sm text-emerald-700">
                <CheckCircle2 className="w-4 h-4" /> {result.creados} colaborador(es) dado(s) de alta correctamente.
              </div>
            )}

            {result.errores?.length > 0 && (
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <p className="text-sm font-medium text-slate-700 flex items-center gap-1">
                    <AlertTriangle className="w-4 h-4 text-amber-500" /> {result.errores.length} fila(s) rechazada(s)
                  </p>
                  <Button size="sm" variant="outline" onClick={downloadErrorLog} data-testid="colab-import-download-errors">
                    <Download className="w-4 h-4 mr-1" /> Descargar log
                  </Button>
                </div>
                <div className="border rounded-lg max-h-56 overflow-y-auto">
                  <table className="w-full text-xs">
                    <thead className="bg-slate-50 text-slate-500 sticky top-0">
                      <tr className="text-left">
                        <th className="px-3 py-2 font-medium">Fila</th>
                        <th className="px-3 py-2 font-medium">CURP</th>
                        <th className="px-3 py-2 font-medium">Motivo</th>
                      </tr>
                    </thead>
                    <tbody>
                      {result.errores.map((e, i) => (
                        <tr key={i} className="border-t">
                          <td className="px-3 py-2">{e.fila}</td>
                          <td className="px-3 py-2 font-mono">{e.curp || '—'}</td>
                          <td className="px-3 py-2 text-slate-600">{e.motivo}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {result.errores?.length === 0 && (
              <div className="flex items-center gap-2 text-sm text-emerald-700">
                <CheckCircle2 className="w-4 h-4" /> Sin errores. Todas las filas fueron procesadas.
              </div>
            )}

            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={reset} data-testid="colab-import-again"><Upload className="w-4 h-4 mr-1" /> Cargar otro</Button>
              <Button onClick={handleClose} data-testid="colab-import-close">Cerrar</Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
